import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PortfolioInsightsPerformance = ({ holdings, baseCurrency, performanceData = [], spyData = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const periods = ['5D', '1M', '3M', '6M', 'ytd', '1Y'];
    const periodLabels = {
        '5D': '5D',
        '1M': '1M',
        '3M': '3M',
        '6M': '6M',
        'ytd': 'YTD',
        '1Y': '1Y'
    };

    // Get unique symbols from holdings
    const symbols = [...new Set(holdings.map(h => h.symbol))];

    // Calculate weighted portfolio performance
    const calculatePortfolioPerformance = (period) => {
        if (performanceData.length === 0) return null;

        let totalValue = 0;
        let weightedReturn = 0;

        holdings.forEach(holding => {
            const stockData = performanceData.find(p => p.symbol === holding.symbol);
            if (stockData && stockData[period] !== undefined) {
                const positionValue = holding.quantity * (holding.quotePrice || holding.price);
                totalValue += positionValue;
                weightedReturn += (stockData[period] || 0) * positionValue;
            }
        });

        return totalValue > 0 ? weightedReturn / totalValue : null;
    };

    // Format percentage
    const formatPercent = (value) => {
        if (value === null || value === undefined) return '--';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}%`;
    };

    // Get color based on value
    const getColor = (value) => {
        if (value === null || value === undefined) return theme.palette.text.secondary;
        if (value > 0) return theme.palette.success.main;
        if (value < 0) return theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    return (
        <Box>
            {/* Benchmark Summary */}
            {(() => {
                const portfolioReturn = calculatePortfolioPerformance('ytd');
                const spyReturn = spyData ? spyData['ytd'] : null;

                if (portfolioReturn === null || spyReturn === null) {
                    return null;
                }

                const difference = portfolioReturn - spyReturn;
                const isOutperforming = difference > 0;
                const isMatching = Math.abs(difference) < 0.01;

                return (
                    <Box sx={{ textAlign: 'center', mb: 3, py: 2 }}>
                        <Typography variant="body1">
                            YTD your portfolio returned{' '}
                            <Typography
                                component="span"
                                fontWeight="bold"
                                sx={{ color: portfolioReturn >= 0 ? theme.palette.success.main : theme.palette.error.main }}
                            >
                                {formatPercent(portfolioReturn)}
                            </Typography>
                            {isMatching ? (
                                ', matching the S&P 500'
                            ) : (
                                <>
                                    ,{' '}
                                    <Typography
                                        component="span"
                                        sx={{ color: isOutperforming ? theme.palette.success.main : theme.palette.error.main }}
                                    >
                                        {isOutperforming ? 'outperforming' : 'underperforming'}
                                    </Typography>
                                    {' '}the S&P 500 by{' '}
                                    <Typography
                                        component="span"
                                        fontWeight="bold"
                                        sx={{ color: isOutperforming ? theme.palette.success.main : theme.palette.error.main }}
                                    >
                                        {Math.abs(difference).toFixed(2)}%
                                    </Typography>
                                </>
                            )}
                        </Typography>
                    </Box>
                );
            })()}

            {/* Performance Table */}
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: isMobile ? 500 : 'auto' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Symbol</TableCell>
                            {periods.map(period => (
                                <TableCell key={period} align="right">
                                    {periodLabels[period]}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {symbols.map(symbol => {
                            const stockPerf = performanceData.find(p => p.symbol === symbol);
                            return (
                                <TableRow key={symbol}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {symbol}
                                        </Typography>
                                    </TableCell>
                                    {periods.map(period => {
                                        const value = stockPerf ? stockPerf[period] : null;
                                        return (
                                            <TableCell key={period} align="right">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: getColor(value) }}
                                                >
                                                    {formatPercent(value)}
                                                </Typography>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                        {/* SPY Benchmark Row */}
                        {spyData && (
                            <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                        S&P 500
                                    </Typography>
                                </TableCell>
                                {periods.map(period => {
                                    const value = spyData[period];
                                    return (
                                        <TableCell key={period} align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{ color: getColor(value) }}
                                            >
                                                {formatPercent(value)}
                                            </Typography>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PortfolioInsightsPerformance;